import { isEqual } from "lodash-es";
import crypto from 'crypto';

import { modalTypes } from "./Modal";
import { socialMediaDomains, searchEngineDomains } from "@/constants/general";

export const SEOLink = "https://en.wikipedia.org/wiki/Search_engine_optimization";

export const uuid = () => {
  const block = () => Math.random().toString(36).replace("0.", "");
  return new Array(4).fill(0).map(block).join("-");
};

export const shareRandomString = () => {
  const newShareId = `DF${(+new Date()).toString(36).slice(-8)}`;

  return newShareId
};

export const productRandomString = () => {
  const newProductId = `pr_${(+new Date()).toString(36).slice(-8)}`;

  return newProductId
};

export const isNumeric = (num) => {
  return (
    (typeof num === "number" ||
      (typeof num === "string" && num.trim() !== "")) &&
    !isNaN(num)
  );
};

export function validatePhoneNumber(phoneNumber) {
  // Define a regular expression for a US phone number
  const phoneRegex = /^\d{10,12}$/;
  // Test the input against the regex
  return phoneRegex.test(phoneNumber);
}

export const isValidUrl = (urlString) => {
  const urlPattern = new RegExp(
    "^(https?:\\/\\/)?" + // Protocol (http, https)
    "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" + // Domain name (www.example.com)
    "((\\d{1,3}\\.){3}\\d{1,3}))" + // OR IP address (IPv4)
    "(\\:\\d+)?" + // Port (optional)
    "(\\/[-a-z\\d%_.~+@]*)*" + // Path, now includes @
    "(\\?[;&a-z\\d%_.~+=-]*)?" + // Query string
    "(\\#[-a-z\\d_]*)?$", // Fragment locator
    "i" // Case insensitive
  );

  return urlPattern.test(urlString);
};

export const getUpdatedProps = (oldObj = {}, newObj = {}, type) => {
  const { duplicating } = modalTypes;
  const updatedProps = {};
  const propsToKeep = {};

  if ("_id" in oldObj) {
    propsToKeep["_id"] = oldObj["_id"] || null;
  }

  if ("share_id" in newObj) {
    propsToKeep["share_id"] = newObj["share_id"] || null;
  }

  if (type === duplicating) {
    return newObj;
  }

  // Check for updated properties
  for (const prop in newObj) {
    const areEqual = isEqual(oldObj[prop], newObj[prop]);

    if (!oldObj.hasOwnProperty(prop) || !areEqual) {
      updatedProps[prop] = newObj[prop];
    }
  }

  return ({
    ...propsToKeep,
    ...updatedProps
  });
};

export class FormData {
  constructor(initData) {
    for (const key in initData) {
      this[key] = initData[key];
    }
  }

  append(prop, value) {
    this[prop] = value;
  }
}

export const fetchTextFile = async (filePath) => {
  const response = await fetch(filePath);
  if (response.ok) {
    const text = await response.text();
    return text;
  }

  return null;
};

export const getTrackUUID = () => {
  if (typeof window !== 'undefined') {
    // User ID management
    let userPrintFingerId = sessionStorage.getItem('uuid');
    if (!userPrintFingerId) {
      userPrintFingerId = uuid(); // Generate unique ID
      sessionStorage.setItem('uuid', userPrintFingerId);
    }

    // Session management
    const sessionKey = 'session';
    let sessionData = JSON.parse(sessionStorage.getItem(sessionKey)) || {};

    const now = Date.now();
    const sessionDuration = 30 * 60 * 1000; // 30 minutes

    if (!sessionData.sessionId || now - sessionData.lastActivity > sessionDuration) {
      // New session or expired session
      sessionData = {
        sessionId: `${userPrintFingerId}_${now}`, // Unique session ID
        lastActivity: now,
      };
      sessionStorage.setItem(sessionKey, JSON.stringify(sessionData));
    } else {
      // Update session activity
      sessionData.lastActivity = now;
      sessionStorage.setItem(sessionKey, JSON.stringify(sessionData));
    }

    return { clientId: userPrintFingerId, sessionId: sessionData.sessionId };
  }
  return null;
};

export const getBrowserAndOSInfo = () => {
  if (typeof window !== 'undefined') {
    const userAgent = navigator.userAgent;
    const platform = navigator.platform;
    let browserName = 'Unknown';
    let os = 'Unknown';
    let deviceType = 'Unknown';

    // Detect browser
    if (userAgent.indexOf("Firefox") > -1) {
      browserName = "Mozilla Firefox";
    } else if (userAgent.indexOf("SamsungBrowser") > -1) {
      browserName = "Samsung Internet";
    } else if (userAgent.indexOf("Opera") > -1 || userAgent.indexOf("OPR") > -1) {
      browserName = "Opera";
    } else if (userAgent.indexOf("Trident") > -1) {
      browserName = "Microsoft Internet Explorer";
    } else if (userAgent.indexOf("Edge") > -1) {
      browserName = "Microsoft Edge";
    } else if (userAgent.indexOf("Chrome") > -1) {
      browserName = "Google Chrome";
    } else if (userAgent.indexOf("Safari") > -1) {
      browserName = "Apple Safari";
    }

    // Detect OS
    if (platform.indexOf("Win") > -1) {
      os = "Windows";
    } else if (platform.indexOf("Mac") > -1) {
      os = "MacOS";
    } else if (platform.indexOf("Linux") > -1) {
      os = "Linux";
    } else if (platform.indexOf("iPhone") > -1 || platform.indexOf("iPad") > -1 || platform.indexOf("iPod") > -1) {
      os = "iOS";
    } else if (userAgent.indexOf("Android") > -1) {
      os = "Android";
    }

    // Detect device type
    if (/Mobi|Android/i.test(userAgent)) {
      deviceType = 'Mobile';
    } else {
      deviceType = 'Desktop';
    }

    return {
      browser: browserName,
      os: os,
      type: deviceType
    };
  }
  return {};
}

// Function to categorize referrer
export function getTrafficSource(referrer) {
  if (!referrer) {
    return ({
      domain: "directTraffic",
      type: "Other"
    });
  }

  const referrerDomain = new URL(referrer).hostname;

  if (socialMediaDomains.some(socialDomain => referrerDomain.includes(socialDomain))) {
    return ({
      domain: referrerDomain,
      type: "Social Media"
    });
  } else if (searchEngineDomains.some(searchDomain => referrerDomain.includes(searchDomain))) {
    return ({
      domain: referrerDomain,
      type: "Search Engine"
    });
  } else {
    return ({
      domain: (!referrerDomain.includes("downfor") ? referrerDomain : "unknown"),
      type: "Other"
    });
  }
}

export const removeEmojis = (str) => {
  if (typeof str === "string") {
    return str.replace(/[\p{Emoji_Presentation}\p{Extended_Pictographic}]/gu, '');
  }

  return null;
}

export const dayDiff = (isoDateTime) => {
  // Parse the ISO datetime string
  const inputDate = new Date(isoDateTime);

  // Get the current time
  const now = new Date();

  // Calculate the difference in time (in milliseconds)
  const diffInMilliseconds = now - inputDate;

  // Convert the difference to days
  const diffInDays = diffInMilliseconds / (1000 * 60 * 60 * 24);

  // Check if the difference is approximately 1 day
  return diffInDays;
};

export function generateKeyFromString(longString) {
  // Create a hash of the string
  const hash = crypto.createHash('sha256'); // Use the SHA-256 algorithm
  hash.update(longString); // Provide the input string
  return hash.digest('hex'); // Output the hash in hexadecimal format
}

export function getDomainWithoutSubdomain(url) {
  try {
    const parsedUrl = new URL(url);  // Parse the URL using the URL object
    const hostname = parsedUrl.hostname;  // Get the full hostname

    // Split the hostname by periods (e.g., 'subdomain.example.com' -> ['subdomain', 'example', 'com'])
    const parts = hostname.split('.');

    // Check if the hostname has more than 2 parts (e.g., "subdomain.example.com")
    if (parts.length > 2) {
      // Return the last two parts (e.g., "example.com")
      return parts.slice(-2).join('.');
    } else {
      // If it's already a domain without a subdomain (e.g., "example.com")
      return hostname;
    }
  } catch (error) {
    console.error("Invalid URL:", error);
    return null;
  }
}

export function logApiRequest(req) {
  const logData = {
    timestamp: new Date().toISOString(),
    method: req.method,
    url: req.url,
    requestId: req.headers['x-amzn-requestid'] || req.headers['x-request-id'] || 'N/A',
    userAgent: req.headers['user-agent'] || 'Unknown',
    ip: req.headers['x-forwarded-for'] || req.connection?.remoteAddress || 'Unknown',
    query: req.query,
    body: req.method !== 'GET' ? req.body : undefined, // Avoid logging body for GET requests
  };

  console.log('[API LOG]', JSON.stringify(logData)); // Pretty-print JSON log
}
